/**
 * Report Studio - Report Editor page.
 * Loads a report by ID and renders the full editor with live preview.
 */

import { createFileRoute } from '@tanstack/react-router';

import { Skeleton } from '@/components/ui/skeleton';
import { useStudioReport } from '@/features/analytics/report-studio/api';
import { ReportEditor } from '@/features/analytics/report-studio/components/ReportEditor';
import i18n from '@/lib/i18n';

export const Route = createFileRoute(
  '/_authenticated/analytics/report-studio/$reportId'
)({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.reportStudioEditor'),
  }),
  component: ReportEditorPage,
});

function ReportEditorPage() {
  const { reportId } = Route.useParams();
  const { data: report, isLoading, error } = useStudioReport(reportId);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <Skeleton className="mb-4 h-8 w-1/3" />
          <div className="flex gap-4">
            <Skeleton className="h-96 w-1/2" />
            <Skeleton className="h-96 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-background p-6 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-md border border-destructive/20 bg-destructive/5 p-8 text-center">
            <p className="text-destructive">
              {error ? 'Failed to load report.' : 'Report not found.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      <div className="mx-auto max-w-full">
        <ReportEditor report={report} />
      </div>
    </div>
  );
}
